import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightCircleIcon } from 'lucide-react';

import { nestedLocationsApi } from '../../../axios';
import { useLanguage } from '../../../hooks';
import { LocationsCreateButton } from '../actionButtons/LocationsCreateButton';

interface LocationSelectorEmptyStateProps {
  variant?: 'searchWithNoResults' | 'noLocationsCreated';
  showAltEmptyState?: boolean;
}

const LocationSelectorEmptyState = ({
  variant,
  showAltEmptyState = false,
}: LocationSelectorEmptyStateProps) => {
  const lang = useLanguage();
  const navigate = useNavigate();
  const { data: accountStatusWithLocations } = useQuery({
    queryKey: ['accountStatusWithLocations'],
    queryFn: () => nestedLocationsApi.checkAccountStatusWithLocations(),
    enabled: variant === 'noLocationsCreated' && !showAltEmptyState,
  });

  const hasOldLocations = accountStatusWithLocations?.has_old_locations ?? false;

  if (showAltEmptyState) {
    return (
      <div className='flex flex-col items-start'>
        <p className='text-[14px] text-gray-500 mb-2 italic'>
          {variant === 'searchWithNoResults'
            ? lang.locations.emptyState.noLocations
            : lang.locations.emptyState.noLocationsCreated}
        </p>
        <LocationsCreateButton variant='emptyStateLocationsSelector2' />
      </div>
    );
  }

  if (variant === 'searchWithNoResults') {
    return (
      <div className='flex flex-col items-center justify-center py-8 px-6'>
        <p className='text-gray-700 text-center'>{lang.locations.emptyState.noLocations}</p>
        <p className='text-sm text-gray-400 text-center mt-1 mb-2'>
          {lang.locations.emptyState.suggestion}
        </p>
        <LocationsCreateButton variant='emptyStateLocationsSelector2' />
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center py-6 px-6'>
      <div className='mb-8'>
        <img
          src='https://data-scope.s3.us-west-2.amazonaws.com/assets/locations/empty-locations-selector.png'
          alt='Empty state illustration'
          className='w-48 max-w-full'
        />
      </div>
      <div className='mb-4 text-lg font-medium text-center text-neutral-10'>
        {hasOldLocations ? (
          <p>{lang.locations.emptyState.oldLocationsTitle}</p>
        ) : (
          <p>{lang.locations.emptyState.createNewTitle}</p>
        )}
        <p>{lang.locations.emptyState.oldLocationsDescription}</p>
      </div>
      <Button
        variant='contained'
        onClick={() => navigate('/locations')}
      >
        {lang.locations.emptyState.createButton}
        <ArrowRightCircleIcon className='w-4 h-4 ml-2' />
      </Button>
    </div>
  );
};

export { LocationSelectorEmptyState };
